import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Disclosure } from "@headlessui/react";
import { Caret } from "@routes/IconeSvg";

import { DisclosureContentFluid } from "@components/TransitionFluid";
import EmptyData from "@components/tailwindComponents/EmptyData";

import { IndividualObjectiveScoreType } from "@helpers/_constants";

import IndividualObjectiveItem from "./IndividualObjectiveItem";
import { MAX_BY_SCORE_TYPE } from "./subComponents/IndividualObjectiveScore/useNotationMapper";
import ModalDeleteIndividualObjective from "./subComponents/ModalDeleteIndividualObjective";

/**
 *
 * @param {Object} props
 * @param {Object[]} props.individualObjectiveList
 * @param {string} props.periodName
 * @param {(individualObjective: any) => void} props.onUpdateObjective
 * @param {boolean} props.readOnly
 * @param {boolean} props.isMyEmployeeSpace
 * @returns
 */
export default function IndividualObjectiveList(props) {
  const { t: translate } = useTranslation("assesment.frequency");

  const { periodName, individualObjectiveList, isMyEmployeeSpace } = props;

  const [superZIndex, setSuperZindex] = useState("");

  const onOpenCloseMenu = (open) => {
    if (open) {
      setSuperZindex("arh-z-10 arh-relative");
    } else {
      setSuperZindex("");
    }
  };

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [toDeleteObjective, setToDeleteObjective] = useState(null);

  function togglModalDelete(individualObjective = null) {
    setToDeleteObjective(individualObjective);
    setShowModalDelete((prevState) => !prevState);
  }

  const scoreTotal = useMemo(() => {
    let score = 0;
    let maxScore = 0;
    for (let individualObjective of individualObjectiveList) {
      if (individualObjective.scoreType === IndividualObjectiveScoreType.NOT_APPLICABLE) {
        continue;
      }

      const _maxScore = MAX_BY_SCORE_TYPE[individualObjective.scoreType];
      const scale = +(100 / _maxScore).toFixed(2);

      score += Math.round(individualObjective.score / scale);
      maxScore += _maxScore;
    }

    return {
      score: score,
      maxScore: maxScore
    };
  }, [individualObjectiveList]);

  return (
    <Disclosure defaultOpen={false}>
      {({ open }) => (
        <>
          <Disclosure.Button className="arh-flex arh-w-full arh-items-center arh-justify-between arh-border-0 arh-bg-greyColor arh-text-left">
            <div className="arh-flex arh-w-full arh-items-center arh-justify-between arh-px-4 arh-py-3">
              <div className="arh-flex arh-items-center">
                <span
                  className={`arh-mx-[1.125rem] arh-rounded-[3.313rem] arh-bg-cardGreen arh-px-[1.125rem] arh-py-[0.094rem] arh-text-center arh-font-poppins arh-font-semibold arh-text-btnGreen arh-text-xs arh-border arh-border-solid arh-border-green`}
                >
                  {individualObjectiveList?.length || '0'}
                </span>
                <span className="arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor">
                  {periodName}
                </span>
              </div>
              <div
                className={`arh-flex arh-transform arh-transition arh-duration-[100ms] arh-ease-in`}
              >
                <Caret
                  className={`${open ? "arh-rotate-90" : "arh-rotate-0"}`}
                />
              </div>
            </div>
          </Disclosure.Button>

          <DisclosureContentFluid
            open={open}
            superZIndex={superZIndex}
          >
            <Disclosure.Panel unmount={false}>
              <table className="arh-my-5 arh-w-full arh-table-fixed arh-font-poppins">
                <thead className="arh-sticky arh-top-0 arh-bg-greyColor">
                  <tr>
                    <th
                      className={`${!props.readOnly ? "arh-w-[20%]" : "arh-w-[25%]"} arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor`}
                    >
                      {translate("Désignation")}
                    </th>
                    <th
                      className={`${!props.readOnly ? "arh-w-[13%]" : "arh-w-[18%]"} arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor`}
                    >
                      {translate("Type")}
                    </th>
                    <th
                      className={`${!props.readOnly ? "arh-w-[18%]" : "arh-w-[22%]"} arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor`}
                    >
                      {translate("Commentaire")}
                    </th>
                    <th className="arh-w-[13%] arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {translate("Fichier joint")}
                    </th>
                    {!props.readOnly && !isMyEmployeeSpace && (
                      <th className="arh-w-[12%] arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor">
                        {translate("Visibilité Employé")}
                      </th>
                    )}
                    <th className="arh-w-[12%] arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {translate("Dernière modification")}
                    </th>
                    <th className="arh-w-[10%] arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {translate("Note")}
                    </th>
                    {!props.readOnly && (
                      <th className="arh-w-[2%] arh-px-2 arh-py-3 arh-text-xs arh-font-semibold arh-text-darkColor"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!individualObjectiveList.length ? (
                    <tr>
                      <td colSpan={props.readOnly ? 6 : (isMyEmployeeSpace ? 7 : 8)}>
                        <EmptyData>
                          {translate("Aucun résultat à afficher")}
                        </EmptyData>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {individualObjectiveList?.map((individualObjective) => (
                        <IndividualObjectiveItem
                          key={individualObjective._id}
                          individualObjective={individualObjective}
                          onOpenCloseMenu={onOpenCloseMenu}
                          onUpdate={props.onUpdateObjective}
                          onDelete={togglModalDelete}
                          readOnly={props.readOnly}
                          isMyEmployeeSpace={isMyEmployeeSpace}
                        />
                      ))}

                      <tr className="border-none">
                        <td
                          colSpan={props.readOnly ? 6 : (isMyEmployeeSpace ? 7 : 8)}
                          className={`${
                            props.readOnly && "arh-pr-14 desktop-l:arh-pr-5"
                          } arh-py-3 arh-text-right arh-text-sm`}
                        >
                          Total: {`${scoreTotal.score}/${scoreTotal.maxScore}`}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>

                <ModalDeleteIndividualObjective
                  isOpen={showModalDelete}
                  onCloseClicked={() => togglModalDelete()}
                  individualObjective={toDeleteObjective}
                />
              </table>
            </Disclosure.Panel>
          </DisclosureContentFluid>
        </>
      )}
    </Disclosure>
  );
}
